import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

export default function HeroSection() {
  const heroRef = useRef<HTMLDivElement>(null);
  const mockupRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();

  useEffect(() => {
    const handleScroll = () => {
      if (!heroRef.current || !mockupRef.current) return;
      const scrollY = window.scrollY;
      mockupRef.current.style.transform = `translateY(${scrollY * 0.12}px)`;
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleDownloadClick = (e: React.MouseEvent) => {
    e.preventDefault();
    const el = document.querySelector("#download");
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      ref={heroRef}
      id="hero"
      className="relative min-h-screen flex items-center overflow-hidden"
      style={{ minHeight: "100svh" }}
    >
      {/* Background Image */}
      <div className="absolute inset-0 z-0">
        <img
          src="/hero-bg.jpg"
          alt="Visipoヒーロー背景"
          className="w-full h-full object-cover object-top"
        />
        {/* Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-black/65 via-black/45 to-[#13B6EC]/20" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />
      </div>

      {/* Floating decorative blobs */}
      <div
        className="absolute top-1/4 right-1/3 w-64 h-64 rounded-full opacity-20 animate-float-slow pointer-events-none"
        style={{ background: "radial-gradient(circle, #13B6EC, transparent 70%)", filter: "blur(40px)" }}
      />
      <div
        className="absolute bottom-1/3 left-1/4 w-48 h-48 rounded-full opacity-15 animate-float-medium pointer-events-none"
        style={{ background: "radial-gradient(circle, #5FCEEF, transparent 70%)", filter: "blur(30px)" }}
      />

      {/* Content */}
      <div className="relative z-10 w-full max-w-7xl mx-auto px-4 md:px-8 pt-24 pb-16 md:pt-28 md:pb-20">
        <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-16">
          {/* Left Text */}
          <div className="flex-1 text-center lg:text-left animate-fade-in-up">
            <div className="inline-flex items-center gap-2 px-3 sm:px-4 py-1.5 rounded-full text-[10px] sm:text-xs font-semibold mb-6 glass-card-subtle border-white/30 text-white/90 tracking-widest uppercase">
              <span className="w-1.5 h-1.5 flex-shrink-0 rounded-full bg-[#13B6EC] animate-pulse-gentle" />
              <span className="truncate">{t('hero.sub')}</span>
            </div>

            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-black leading-tight mb-3">
              <span
                className="block text-white"
                style={{ fontFamily: "'Noto Sans JP', sans-serif" }}
              >
                {t('hero.title1')}
              </span>
              <span
                className="block"
                style={{
                  fontFamily: "'Noto Serif JP', serif",
                  color: "#5FCEEF",
                  fontWeight: 700,
                  fontStyle: "italic",
                }}
              >
                {t('hero.title2')}
              </span>
            </h1>

            <p
              className="text-sm sm:text-base md:text-lg text-white/80 max-w-md mx-auto lg:mx-0 mt-5 mb-8 leading-relaxed"
              style={{ fontFamily: "'Noto Sans JP', sans-serif" }}
            >
              {t('hero.desc')}
            </p>

          </div>

          {/* Right Mockup */}
          <div ref={mockupRef} className="flex-shrink-0 flex justify-center lg:justify-end relative">
            {/* Glow circle behind */}
            <div
              className="absolute inset-0 rounded-full opacity-30 pointer-events-none"
              style={{
                background: "radial-gradient(circle, #13B6EC 0%, transparent 70%)",
                filter: "blur(50px)",
                transform: "scale(1.3)",
              }}
            />
            {/* Phone mockup frame */}
            <div
              className="relative z-10"
              style={{
                filter: "drop-shadow(0 30px 60px rgba(0,0,0,0.5))",
              }}
            >
              <div
                className="rounded-[36px] overflow-hidden"
                style={{
                  width: "260px",
                  height: "520px",
                  border: "8px solid rgba(255,255,255,0.2)",
                  boxShadow: "0 0 0 1px rgba(255,255,255,0.05), inset 0 0 0 1px rgba(255,255,255,0.1)",
                }}
              >
                <img
                  src="/hero-mockup.png"
                  alt="Visipoアプリ画面"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              {/* Floating badges */}
              <div
                className="absolute -top-4 -left-8 glass-card rounded-2xl px-3 py-2 animate-float-medium"
                style={{ boxShadow: "0 4px 20px rgba(0,0,0,0.15)" }}
              >
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#13B6EC]/10">
                    <i className="ri-footprint-fill text-[#13B6EC] text-sm" />
                  </div>
                  <div>
                    <div className="text-[10px] text-gray-400">{t('hero.stats_label')}</div>
                    <div className="text-sm font-bold text-gray-800">{t('hero.stats_value')}</div>
                  </div>
                </div>
              </div>
              <div
                className="absolute bottom-12 -right-10 glass-card rounded-2xl px-3 py-2 animate-float-slow"
                style={{ boxShadow: "0 4px 20px rgba(0,0,0,0.15)" }}
              >
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 flex items-center justify-center rounded-full bg-yellow-50">
                    <i className="ri-medal-fill text-yellow-500 text-sm" />
                  </div>
                  <div>
                    <div className="text-[10px] text-gray-400">{t('hero.badge_sub')}</div>
                    <div className="text-xs font-bold text-gray-800">{t('hero.badge_title')}</div>
                  </div>
                </div>
              </div>
              <div
                className="absolute top-[60%] -left-6 glass-card rounded-2xl px-3 py-2 animate-float-medium z-20"
                style={{ animationDelay: "1s", boxShadow: "0 4px 20px rgba(0,0,0,0.15)" }}
              >
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 flex items-center justify-center rounded-full bg-[#13B6EC]/10">
                    <i className="ri-quill-pen-fill text-[#13B6EC] text-xs" />
                  </div>
                  <div className="text-xs font-semibold text-gray-700">{t('hero.diary_gen')}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2 animate-bounce-slow">
        <span className="text-xs text-white/50 tracking-widest uppercase">{t('hero.scroll')}</span>
        <i className="ri-arrow-down-line text-white/60 text-lg" />
      </div>
    </section>
  );
}
